import React, { useState, useEffect, useCallback } from 'react';
import {
    AlertCircle, CheckCircle, XCircle, Search, RefreshCw,
    ArrowRight, User, Phone, Calendar, DollarSign,
    FileText, Hash, Clock, Link, Trash2, Filter,
    ChevronLeft, ChevronRight, Building2, Users, TrendingUp
} from 'lucide-react';
import api from '../services/api';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const UnattributedPayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    const [stats, setStats] = useState(null);
    const [filterStatus, setFilterStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showMatchModal, setShowMatchModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [leases, setLeases] = useState([]);
    const [selectedLeaseId, setSelectedLeaseId] = useState('');
    const [matching, setMatching] = useState(false);
    const [autoMatching, setAutoMatching] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page, limit: 20 };
            if (filterStatus) params.status = filterStatus;
            if (searchTerm) params.search = searchTerm;

            const response = await api.get('/unattributed-payments', { params });
            if (response.data.success) {
                setPayments(response.data.data.data || []);
                setPagination(response.data.data.pagination || null);
            }
        } catch (error) {
            console.error('Error fetching unattributed payments:', error);
            setMessage({ type: 'error', text: 'Failed to load unattributed payments' });
        } finally {
            setLoading(false);
        }
    }, [page, filterStatus, searchTerm]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/unattributed-payments/stats');
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, []);

    const fetchLeases = async () => {
        try {
            const response = await api.get('/leases', { params: { limit: 200 } });
            if (response.data.success) {
                const leasesData = [];
                if (response.data.data) {
                    if (Array.isArray(response.data.data)) {
                        leasesData.push(...response.data.data);
                    } else if (response.data.data.data) {
                        leasesData.push(...response.data.data.data);
                    }
                }
                setLeases(leasesData);
            }
        } catch (error) {
            console.error('Error fetching leases:', error);
        }
    };

    useEffect(() => {
        fetchPayments();
        fetchStats();
    }, [fetchPayments, fetchStats]);

    const openMatchModal = async (payment) => {
        setSelectedPayment(payment);
        setSelectedLeaseId('');
        await fetchLeases();
        setShowMatchModal(true);
    };

    const handleMatch = async () => {
        if (!selectedLeaseId) return;
        try {
            setMatching(true);
            const response = await api.post(`/unattributed-payments/${selectedPayment.id}/match`, {
                lease_id: parseInt(selectedLeaseId)
            });
            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: `✅ KES ${parseFloat(selectedPayment.amount).toLocaleString()} matched successfully! Payment #${response.data.data.payment_id} created.`
                });
                setShowMatchModal(false);
                fetchPayments();
                fetchStats();
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to match payment' });
        } finally {
            setMatching(false);
        }
    };

    const handleAutoMatch = async () => {
        try {
            setAutoMatching(true);
            const response = await api.post('/unattributed-payments/auto-match');
            if (response.data.success) {
                setMessage({
                    type: 'success',
                    text: `✅ Auto-matched ${response.data.data.matched} payment(s)`
                });
                fetchPayments();
                fetchStats();
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Auto-match failed' });
        } finally {
            setAutoMatching(false);
        }
    };

    const handleIgnore = async (id) => {
        if (!window.confirm('Ignore this payment? It will be hidden from the list.')) return;
        try {
            await api.post(`/unattributed-payments/${id}/ignore`);
            setMessage({ type: 'success', text: 'Payment ignored' });
            fetchPayments();
            fetchStats();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to ignore payment' });
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Pending
                </span>;
            case 'matched':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Matched
                </span>;
            case 'ignored':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 flex items-center gap-1">
                    <XCircle className="h-3 w-3" /> Ignored
                </span>;
            default:
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
        }
    };

    const StatCard = ({ label, value, icon: Icon, color }) => (
        <div className="bg-white rounded-lg shadow-md p-6 flex items-center space-x-4 border border-gray-100">
            <div className={`p-3 rounded-full ${color}`}>
                <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
                <p className="text-sm font-medium text-gray-500">{label}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <AlertCircle className="h-7 w-7 text-orange-500" />
                        Unattributed Payments
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        M-Pesa Paybill/Till payments that couldn't be automatically matched. 
                        Review and manually link them to the correct lease.
                    </p>
                </div>
                <button
                    onClick={handleAutoMatch}
                    disabled={autoMatching}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${autoMatching ? 'animate-spin' : ''}`} />
                    {autoMatching ? 'Matching...' : 'Auto-Match All'}
                </button>
            </div>

            {/* Message */}
            {message.text && (
                <div className={`p-4 rounded-lg flex items-center justify-between ${
                    message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 
                    'bg-red-50 text-red-800 border border-red-200'
                }`}>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="text-gray-500 hover:text-gray-700">
                        <XCircle className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <StatCard
                        label="Total Unattributed"
                        value={stats.total || 0}
                        icon={AlertCircle}
                        color="bg-orange-500"
                    />
                    <StatCard
                        label="Pending"
                        value={stats.pending_count || 0}
                        icon={Clock}
                        color="bg-yellow-500"
                    />
                    <StatCard
                        label="Total Amount (Pending)"
                        value={`KES ${parseFloat(stats.pending_amount || 0).toLocaleString()}`}
                        icon={DollarSign}
                        color="bg-red-500"
                    />
                    <StatCard
                        label="Amount (All)"
                        value={`KES ${parseFloat(stats.total_amount || 0).toLocaleString()}`}
                        icon={TrendingUp}
                        color="bg-blue-500"
                    />
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center flex-1 min-w-[200px]">
                        <Search className="h-5 w-5 text-gray-400 mr-2" />
                        <input
                            type="text"
                            placeholder="Search phone, name, bill ref, trans ID..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter className="h-5 w-5 text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="matched">Matched</option>
                            <option value="ignored">Ignored</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                {loading ? (
                    <LoadingSpinner />
                ) : payments.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-400" />
                        <p className="text-lg font-medium">No unattributed payments</p>
                        <p className="text-sm">All payments have been matched or none have been received yet.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sender</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill Ref</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trans ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {payments.map((payment) => (
                                    <tr key={payment.id} className={`hover:bg-gray-50 ${
                                        payment.status === 'ignored' ? 'opacity-50' : ''
                                    }`}>
                                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(payment.status)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">
                                            KES {parseFloat(payment.amount).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="flex items-center gap-1">
                                                <User className="h-4 w-4 text-gray-400" />
                                                {[payment.first_name, payment.middle_name, payment.last_name].filter(Boolean).join(' ') || 'Unknown'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="flex items-center gap-1">
                                                <Phone className="h-4 w-4 text-gray-400" />
                                                {payment.msisdn || '—'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-mono">
                                                {payment.bill_ref_number || '—'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                            {payment.trans_id?.substring(0, 12)}...
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="h-4 w-4 text-gray-400" />
                                                {new Date(payment.created_at).toLocaleDateString()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {payment.status === 'pending' ? (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => openMatchModal(payment)}
                                                        className="flex items-center px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 text-sm"
                                                    >
                                                        <Link className="h-4 w-4 mr-1" />
                                                        Match
                                                    </button>
                                                    <button
                                                        onClick={() => handleIgnore(payment.id)}
                                                        className="flex items-center px-3 py-1.5 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                                                    >
                                                        <XCircle className="h-4 w-4 mr-1" />
                                                        Ignore
                                                    </button>
                                                </div>
                                            ) : payment.status === 'matched' ? (
                                                <span className="text-sm text-green-600 flex items-center gap-1">
                                                    <CheckCircle className="h-4 w-4" />
                                                    Lease #{payment.matched_lease_id}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-400">Ignored</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
                <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-500">
                        Page {pagination.page} of {pagination.total_pages} ({pagination.total} records)
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                            disabled={page >= pagination.total_pages}
                            className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Match Modal */}
            <Modal isOpen={showMatchModal} onClose={() => setShowMatchModal(false)} title="Match Payment to Lease" size="lg">
                {selectedPayment && (
                    <div className="space-y-6">
                        {/* Payment Details */}
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <h4 className="font-medium text-blue-900 mb-2">Payment Details</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-blue-600">Amount:</span>
                                    <p className="font-bold text-lg">KES {parseFloat(selectedPayment.amount).toLocaleString()}</p>
                                </div>
                                <div>
                                    <span className="text-blue-600">From:</span>
                                    <p>{[selectedPayment.first_name, selectedPayment.last_name].filter(Boolean).join(' ') || 'Unknown'}</p>
                                </div>
                                <div>
                                    <span className="text-blue-600">Phone:</span>
                                    <p>{selectedPayment.msisdn || 'N/A'}</p>
                                </div>
                                <div>
                                    <span className="text-blue-600">Bill Ref:</span>
                                    <p className="font-mono">{selectedPayment.bill_ref_number || 'N/A'}</p>
                                </div>
                                <div>
                                    <span className="text-blue-600">Trans ID:</span>
                                    <p className="font-mono text-xs">{selectedPayment.trans_id}</p>
                                </div>
                                <div>
                                    <span className="text-blue-600">Date:</span>
                                    <p>{new Date(selectedPayment.created_at).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        {/* Lease Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Lease to Match
                            </label>
                            <select
                                value={selectedLeaseId}
                                onChange={(e) => setSelectedLeaseId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">-- Select a lease --</option>
                                {leases.map((lease) => (
                                    <option key={lease.id} value={lease.id}>
                                        #{lease.lease_number} — {lease.first_name || ''} {lease.last_name || ''} 
                                        {lease.unit_number ? ` — ${lease.unit_number}` : ''}
                                        {lease.asset_name ? ` — ${lease.asset_name}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 pt-4 border-t">
                            <button
                                onClick={() => setShowMatchModal(false)}
                                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMatch}
                                disabled={!selectedLeaseId || matching}
                                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {matching ? (
                                    <>Processing...</>
                                ) : (
                                    <><Link className="h-4 w-4 mr-2" />Create Payment & Match</>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default UnattributedPayments;
